/**
 * Carousel Plugin for Search Results Map Variant
 *
 * Renders a bottom-sheet carousel when the user taps a marker on mobile.
 * Cards are populated synchronously for spec / plan / community markers, or
 * asynchronously via an epic for buildable-plan (community) markers.
 *
 * Single responsibility: display and dismiss the mobile card carousel in
 * response to marker tap interactions.
 *
 * This plugin does NOT:
 * - Run on desktop (bails out in start() when isDesktop() is true)
 * - Handle the hover tooltip (see miniMapCard.plugin)
 * - Dispatch the marker click or open URLs (see markers.plugin)
 * - Manage the map canvas or marker layer (see map.plugin, markers.plugin)
 *
 * Reason to change: carousel rendering logic, mobile card variants, the
 * buildable-plan async fetch flow, or touch-dismiss behaviour.
 *
 * @module searchResultsMapVariant/plugins/carousel.plugin
 */

import { distinctUntilChanged, filter, map } from 'rxjs';
import fetchBuildablePlansMiniCardsEpic from '../redux/fetchBuildablePlansMiniCardsEpic';
import {
  selectFetchResultsStatus,
  selectMarkerCards,
  selectSelectedMarker,
} from '../redux/selectors';
import MarkerType from '../enums/markerType';
import mapDataSlice from '../redux/dataSlice';
import mapInteractionSlice from '../redux/interactionSlice';
import MobileMapCarousel from '../components/mobileMapCarousel';
import { mapMarkerToCardInformation } from '../services/mapper';
import {
  MAX_VISIBLE_PLANS,
  createCardElements,
  createSeeAllPlansElement,
  createSkeletonCardElement,
} from '../components/carouselDom';

const { actions: dataActions } = mapDataSlice;
const { actions: interactionActions } = mapInteractionSlice;

const OWNER_ID = 'map-carousel';

/**
 * Creates the carousel plugin.
 *
 * @returns {{ name: string, init: Function, start: Function, stop: Function }}
 */
export default function createCarouselPlugin() {
  const pluginState = {
    started: false,
    mobileCarousel: null,
    isDesktopBreakpoint: null,
    dispatch: null,
    store$: null,
    subscriptions: [],
    skeletonCard: null,
  };

  function renderMobileCards(cards) {
    const isCarouselAvailable = pluginState.mobileCarousel?.isAvailable;
    const hasValidCards = cards?.length > 0;

    if (!isCarouselAvailable || !hasValidCards) {
      return;
    }

    const firstCard = cards[0];
    const cardElements = createCardElements(cards);
    const needsSeeAllButton = firstCard.totalPlans > MAX_VISIBLE_PLANS &&
      firstCard.type === MarkerType.BuildablePlan.toString();

    if (needsSeeAllButton) {
      const seeAllElement = createSeeAllPlansElement(firstCard);
      if (seeAllElement) {
        cardElements.push(seeAllElement);
      }
    }

    pluginState.mobileCarousel.render(cardElements, cards, OWNER_ID);
  }

  function renderSkeletonCard() {
    if (!pluginState.mobileCarousel?.isAvailable) {
      return;
    }

    pluginState.mobileCarousel.render([pluginState.skeletonCard], [], OWNER_ID);
  }

  /**
   * Reacts to a marker being tapped. For buildable-plan markers an async fetch
   * is triggered; for all other marker types the card data is resolved
   * synchronously from the store.
   *
   * @param {Object} selectedMarker - The selected marker object from Redux.
   */
  function handleMarkerSelection(selectedMarker) {
    const { id: selectedMarkerId, type: selectedMarkerType } = selectedMarker;

    if (selectedMarkerType === MarkerType.BuildablePlan.toString()) {
      pluginState.dispatch(
        dataActions.fetchBuildablePlans({
          communityId: selectedMarkerId,
        }),
      );

      renderSkeletonCard();
      return;
    }

    const metadata = { ...mapMarkerToCardInformation(selectedMarker), type: selectedMarkerType };
    pluginState.dispatch(interactionActions.setMarkerCards([metadata]));
  }

  function handleMobileCards(cards) {
    if (!cards?.length) {
      pluginState.mobileCarousel?.clear(OWNER_ID);
      return;
    }

    renderMobileCards(cards);
  }

  function setupSubscriptions() {
    // React to tap: selectedMarker is set by markers.plugin on mobile tap.
    pluginState.subscriptions.push(
      pluginState.store$.pipe(
        filter(() => !(pluginState.isDesktopBreakpoint?.() ?? false)),
        map(selectSelectedMarker),
        distinctUntilChanged(),
        filter(Boolean),
      ).subscribe((selectedMarker) => {
        handleMarkerSelection(selectedMarker);
      }),
    );

    // Render carousel when Redux state is updated with new marker card data.
    pluginState.subscriptions.push(
      pluginState.store$.pipe(
        map(selectMarkerCards),
        distinctUntilChanged(),
      ).subscribe((cards) => {
        handleMobileCards(cards);
      }),
    );

    // Clear cards when the layout breakpoint changes (e.g. desktop ↔ mobile resize).
    // Prevents stale carousel state persisting across viewport transitions.
    pluginState.subscriptions.push(
      pluginState.store$.pipe(
        map(() => pluginState.getBreakpoint()),
        distinctUntilChanged(),
      ).subscribe(() => {
        pluginState.dispatch(interactionActions.clearMarkerCards());
      }),
    );

    // Dismiss the carousel when new search results are fetched.
    pluginState.subscriptions.push(
      pluginState.store$.pipe(
        map(selectFetchResultsStatus),
        distinctUntilChanged(),
      ).subscribe(() => {
        if (pluginState.mobileCarousel?.isVisible()) {
          pluginState.dispatch(interactionActions.resetSelectedMarkerAndCarouselCards());
        }
      }),
    );
  }

  function setupTouchSubscription() {
    const handleTouchStart = (event) => {
      if (!pluginState.mobileCarousel.isVisible()) {
        return;
      }

      const isInsideCarousel = !!event.target.closest('[data-map-carousel]');
      const isMarkerIcon = !!event.target.closest('[data-marker-icon]');
      if (!isInsideCarousel && !isMarkerIcon) {
        pluginState.dispatch(interactionActions.resetSelectedMarkerAndCarouselCards());
      }
    };

    pluginState.container.addEventListener('touchstart', handleTouchStart);

    pluginState.subscriptions.push({
      unsubscribe: () => pluginState.container.removeEventListener('touchstart', handleTouchStart),
    });
  }

  function clearSubscriptions() {
    pluginState.subscriptions.forEach((subscription) => subscription?.unsubscribe?.());
    pluginState.subscriptions = [];
  }

  return {
    name: 'map-carousel',

    async init(context) {
      context.services.epic$.next(fetchBuildablePlansMiniCardsEpic);
      context.state.mapCarousel = { started: false };
    },

    async start(context) {
      if (pluginState.started) {
        return;
      }

      pluginState.dispatch = context.services.dispatch;
      pluginState.store$ = context.services.store$;
      pluginState.getBreakpoint = context.services.getBreakpoint;
      pluginState.isDesktopBreakpoint = context.services.isDesktopBreakpoint;
      pluginState.container = context.root;

      pluginState.mobileCarousel = new MobileMapCarousel((detail) => {
        document.dispatchEvent(new CustomEvent('carouselScroll', { detail }));
      });

      // skeletonCard created before subscriptions so renderSkeletonCard never
      // references a null element if a synchronous store emission fires during setup.
      pluginState.skeletonCard = createSkeletonCardElement();
      setupSubscriptions();
      setupTouchSubscription();

      pluginState.started = true;
      context.state.mapCarousel = { started: true };
    },

    async stop(context) {
      // Dispatch before clearing subscriptions so the cards subscription
      // can react and imperatively clear the carousel.
      pluginState.dispatch(interactionActions.resetSelectedMarkerAndCarouselCards());

      clearSubscriptions();

      pluginState.mobileCarousel?.destroy();
      pluginState.mobileCarousel = null;

      pluginState.dispatch = null;
      pluginState.store$ = null;
      pluginState.isDesktopBreakpoint = null;
      pluginState.started = false;
      pluginState.skeletonCard = null;
      pluginState.container = null;

      delete context.state.mapCarousel;
    },
  };
}
